import isString from 'lodash/isString';
import toNumber from 'lodash/toNumber';

import { numberInputStringToNumber } from 'common/formatting/numberInput';
import { VatFieldValue } from 'modules/menu/components/VatCategories';

import { OptionFormData } from '../../../types';

type ReduxFormValidator = (value: any, allValues?: object) => string | undefined;
type Validator = (input: any, allValues?: object) => boolean;

const chainReduxFormValidators =
  (validators: ReduxFormValidator[]): ReduxFormValidator =>
  (...args) => {
    for (const validator of validators) {
      const result = validator(...args);
      if (result) {
        return result;
      }
    }
    return;
  };

const isNumberString: Validator = (input: any): input is string =>
  isString(input) && input !== '' && isFinite(toNumber(input.replace(',', '.')));

const isInteger: Validator = (input: string) => +input === Math.floor(+input);

const isIntegerString: Validator = (input: string): input is string =>
  isNumberString(input) && isInteger(toNumber(input));

const isNonNegativeNumberString: Validator = (input: string): input is string =>
  isNumberString(input) && toNumber(input.replace(',', '.')) >= 0;

const buildIsNumberStringLte =
  (max: number): Validator =>
  (input: any): input is string =>
    isNumberString(input) && toNumber(input.replace(',', '.')) <= max;

const isNumberStringLteHundred = buildIsNumberStringLte(100);

const notEmpty: Validator = (input: any) => input != null && ('' + input).trim().length > 0;

const reduxFormValidation = (
  validator: (input: any, allValues?: object) => boolean,
  errorMsg: string,
): ((value: any, allValues?: object) => string | undefined) => {
  return (...args) => (validator(...args) ? undefined : errorMsg);
};

const requiredValidator = reduxFormValidation(notEmpty, 'validation.required');

const numberValidator = reduxFormValidation(isNumberString, 'validation.mustBeNumber');

const countValidator = chainReduxFormValidators([
  reduxFormValidation(isNumberString, 'validation.mustBeNumber'),
  reduxFormValidation(isIntegerString, 'validation.mustBeInteger'),
  reduxFormValidation(isNonNegativeNumberString, 'validation.cantBeNegative'),
]);

const requiredCountValidator = chainReduxFormValidators([
  reduxFormValidation(notEmpty, 'validation.required'),
  countValidator,
]);

const optionalCountValidator = (value: any, allValues?: object): string | undefined => {
  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }
  return countValidator(value, allValues);
};

const maxCountGteMinCountDependentValidator = (maxCount: string, minCount: string) => {
  if (requiredCountValidator(maxCount) !== undefined || requiredCountValidator(minCount) !== undefined) {
    return undefined;
  }

  return numberInputStringToNumber(minCount) <= numberInputStringToNumber(maxCount)
    ? undefined
    : 'menu-option.max-count-larger';
};

const hasDecimalsAtMost =
  (numDecimals: number): Validator =>
  (input: string) =>
    new RegExp(`^-?\\d+(\\.\\d{0,${numDecimals}})?$`).test(input.replace(',', '.').trim());

const twoDecimalsValidator = reduxFormValidation(hasDecimalsAtMost(2), 'validation.atMostTwoDecimals');

const betweenZeroAndHundredValidator = reduxFormValidation(
  (value: any): value is string => isNonNegativeNumberString(value) && isNumberStringLteHundred(value),
  'validation.mustBePercentage',
);

const optionalVatPercentageValidator = (value: VatFieldValue['percentage'], allValues?: object): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  return vatPercentageValidator(String(value), allValues);
};

const vatPercentageValidator = chainReduxFormValidators([
  numberValidator,
  twoDecimalsValidator,
  betweenZeroAndHundredValidator,
]);

const maximumSelectionsValidator = (maxCount: string, minCount: string) => {
  return optionalCountValidator(maxCount) || maxCountGteMinCountDependentValidator(maxCount, minCount);
};

export const validateOptionForm = (formData: OptionFormData) => {
  return {
    name: requiredValidator(formData.name),
    values:
      formData.type === 'choice' || formData.type === 'multi_choice'
        ? formData.values.map(({ maximum_selections, minimum_selections, name, price, vat }) => {
            return {
              name: requiredValidator(name),
              price: numberValidator(price),
              vat: optionalVatPercentageValidator(vat?.percentage),
              ...(formData.type === 'multi_choice' && {
                maximum_selections: maximumSelectionsValidator(maximum_selections, minimum_selections),
                minimum_selections: requiredCountValidator(minimum_selections),
              }),
            };
          })
        : [],
    boolValues:
      formData.type === 'bool'
        ? formData.boolValues.map(({ name, price, vat }) => {
            return {
              name: requiredValidator(name),
              price: numberValidator(price),
              vat: optionalVatPercentageValidator(vat?.percentage),
            };
          })
        : [],
  };
};
